/**
 * API client for the notifications endpoints (WI-56).
 *
 * Endpoints:
 *   GET  /api/v1/notifications              → list recent notifications
 *   GET  /api/v1/notifications/unread-count → count of unread notifications
 *   POST /api/v1/notifications/read-all     → mark all unread as read
 *   POST /api/v1/notifications/read         → mark specific notifications as read
 *
 * All endpoints require authentication and throw AuthApiError on 401.
 */

import { AuthApiError } from './auth';

export type NotificationEventType = 'score_tier_changed' | 'new_camp';

export interface NotificationItem {
  id: string;
  event_type: NotificationEventType;
  school_slug: string;
  school_name: string;
  sport_slug: string | null;
  target_year: number | null;
  position_slug: string | null;
  old_tier: string | null;
  new_tier: string | null;
  camp_name: string | null;
  camp_start_date: string | null;
  camp_registration_url: string | null;
  is_read: boolean;
  created_at: string;
}

export interface UnreadCountResponse {
  count: number;
}

export interface MarkReadResponse {
  marked_read: number;
}

async function parseNotificationsError(response: Response): Promise<AuthApiError> {
  try {
    const body: { error: string; code: string } = await response.json();
    return new AuthApiError(body.error, body.code, response.status);
  } catch {
    return new AuthApiError(response.statusText, 'unknown', response.status);
  }
}

/**
 * Fetch the current user's unread notification count.
 * Throws AuthApiError on 401 (unauthenticated).
 */
export async function fetchUnreadCount(): Promise<UnreadCountResponse> {
  const response = await fetch('/api/v1/notifications/unread-count', {
    credentials: 'include',
  });
  if (!response.ok) throw await parseNotificationsError(response);
  return response.json();
}

/**
 * Fetch the current user's recent notifications.
 * Returns unread rows first, then read rows, sorted by created_at DESC within each group.
 * Throws AuthApiError on 401 (unauthenticated).
 */
export async function fetchNotifications(limit = 20): Promise<NotificationItem[]> {
  const url = `/api/v1/notifications?limit=${limit}`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  if (!response.ok) throw await parseNotificationsError(response);
  return response.json();
}

/**
 * Mark all unread notifications as read for the current user.
 * Returns the count of rows updated. Idempotent.
 * Throws AuthApiError on 401 (unauthenticated).
 */
export async function markAllRead(): Promise<MarkReadResponse> {
  const response = await fetch('/api/v1/notifications/read-all', {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw await parseNotificationsError(response);
  return response.json();
}

/**
 * Mark specific notification IDs as read.
 * Ignores IDs that don't belong to the current user.
 * Throws AuthApiError on 401 (unauthenticated).
 */
export async function markRead(ids: string[]): Promise<MarkReadResponse> {
  const response = await fetch('/api/v1/notifications/read', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) throw await parseNotificationsError(response);
  return response.json();
}
